import { useMemo } from 'react';
import {
  QueryHookOptions,
  BaseQueryOptions,
  LazyQueryHookOptions,
  ApolloError,
} from '@apollo/client';

import {
  useRetailerNetworksForUserQuery as useApolloRetailerNetworksForUserQuery,
  RetailerNetworksForUserQuery,
  RetailerNetworksForUserQueryVariables,
  RetailersNetworksFilter,
  RetailerNetworkUsersQuery,
  RetailerNetworkUsersQueryVariables,
  RetailerNetworkUserFragment,
  RetailerNetworkUsersFilter,
  useRetailerNetworkUsersQuery,
  useRetailerNetworkUsersLazyQuery,
  RetailerNetworkPartialFragment,
  useRetailerNetworkQuery as useApolloRetailerNetworkQuery,
  RetailerNetworkQueryVariables,
  RetailerNetworkCustomerQueryVariables,
  RetailerNetworkCustomerQuery,
  RetailerNetworkCustomerFragment,
  useRetailerNetworkCustomerLazyQuery,
  RetailerNetworkLocationFilter,
  RetailerNetworkLocationFragment,
  RetailerNetworkLocationsQueryVariables,
  RetailerNetworkLocationsQuery,
  useRetailerNetworkLocationsQuery,
  useRetailerNetworkLocationsLazyQuery,
  RetailerNetworkLocationDetailsFragment,
  RetailerNetworkLocationDetailsQueryVariables,
  RetailerNetworkLocationDetailsQuery,
  useRetailerNetworkLocationDetailsQuery,
  RetailerNetworkLocationGroupFilter,
  RetailerNetworkLocationGroupsQueryVariables,
  RetailerNetworkLocationGroupsQuery,
  useRetailerNetworkLocationGroupsQuery,
  useRetailerNetworkLocationGroupsLazyQuery,
  RetailerNetworkLocationsWithPerformanceExcerptQueryVariables,
  RetailerNetworkLocationsWithPerformanceExcerptQuery,
  RetailerNetworkLocationsWithPerformanceExcerptFilter,
  RetailerNetworkLocationWithPerformanceExcerptFragment,
  useRetailerNetworkLocationsWithPerformanceExcerptQuery,
  RetailerNetworkPerformanceQueryVariables,
  useRetailerNetworkPerformanceQuery as useApolloRetailerNetworkPerformanceQuery,
  useRetailerNetworksForUserQuery,
  RetailerNetworkLocationGroupFragment,
  useRetailerNetworksForUserLazyQuery,
  RetailerNetworkPerformanceSort,
  RetailerNetworkLocationDowntimeMetricsQueryVariables,
  useRetailerNetworkLocationDowntimeMetricsQuery as useApolloRetailerNetworkLocationDowntimeMetricsQuery,
  RetailerNetworkLocationDowntimeMetricsOverviewQueryVariables,
  useRetailerNetworkLocationDowntimeMetricsOverviewQuery as useApolloRetailerNetworkLocationDowntimeMetricsOverviewQuery,
  RetailerNetworkLocationGroupOnlineStatusQueryVariables,
  useRetailerNetworkLocationGroupOnlineStatusQuery as useApolloRetailerNetworkLocationGroupOnlineStatusQuery,
  RetailerNetworkTransactionMetricsOverviewQueryVariables,
  useRetailerNetworkTransactionMetricsOverviewQuery as useApolloRetailerNetworkTransactionMetricsOverviewQuery,
  RetailerNetworkLoyaltyMetricsOverviewQueryVariables,
  useRetailerNetworkLoyaltyMetricsOverviewQuery as useApolloRetailerNetworkLoyaltyMetricsOverviewQuery,
  RetailerNetworkOfferMetricsOverviewFilter,
  RetailerNetworkOfferMetricsOverviewSort,
  RetailerNetworkLocationOfferMetricsOverviewQuery,
  RetailerNetworkLocationOfferMetricsOverviewQueryVariables,
  useRetailerNetworkLocationOfferMetricsOverviewQuery as useApolloRetailerNetworkLocationOfferMetricsOverviewQuery,
  TimeSeriesMetricFragment,
  RetailerNetworkLocationCoordinatesFilter,
  RetailerNetworkLocationCoordinatesFragment,
  RetailerNetworkLocationCoordinatesQuery,
  RetailerNetworkLocationCoordinatesQueryVariables,
  useRetailerNetworkLocationCoordinatesQuery,
  RetailerNetworkLocationPosConnectionStatusQueryVariables,
  useRetailerNetworkLocationPosConnectionStatusQuery as useApolloRetailerNetworkLocationPosConnectionStatusQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { IPaginationProps } from '@occc/ui-design-system';
import config from '@occc/config';
import { useLazyQuery, useQuery } from '@occc-app/dataAccessLayer/utils';

export const useGetRetailerNetworkQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkQueryVariables>,
  skip?: boolean,
) =>
  useQuery(useApolloRetailerNetworkQuery, {
    skip: skip || !config.retailerConnect.enabled,
    ...baseOptions,
  });

export const useGetRetailerNetworksForUserQuery = (
  baseOptions?: QueryHookOptions<
    RetailerNetworksForUserQuery,
    RetailerNetworksForUserQueryVariables
  >,
) =>
  useQuery(useRetailerNetworksForUserQuery, {
    skip: !config.retailerConnect.enabled,
    ...baseOptions,
  });

interface GetRetailerNetworksProps {
  filter: RetailersNetworksFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkPartialFragment[],
    RetailerNetworksForUserQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformDataToRetailerNetworksArray = (
  data: RetailerNetworksForUserQuery,
): RetailerNetworkPartialFragment[] => {
  const edges = data.retailerNetworksForUser.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworksForUserQueryPaginated = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworksProps) => {
  const response = usePaginatedQuery(
    useApolloRetailerNetworksForUserQuery,
    'retailerNetworksForUser.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToRetailerNetworksArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToRetailerNetworksArray(response.data) : response.data,
  };
};

interface GetRetailerNetworkUsersProps {
  filter: RetailerNetworkUsersFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<RetailerNetworkUserFragment[], RetailerNetworkUsersQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
}

const transformDataToRetailerNetworkUsersArray = (
  data: RetailerNetworkUsersQuery,
): RetailerNetworkUserFragment[] => {
  const edges = data.retailerNetworkUsers?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkUsers = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetRetailerNetworkUsersProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkUsersQuery,
    'retailerNetworkUsers.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToRetailerNetworkUsersArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToRetailerNetworkUsersArray(response.data) : response.data,
  };
};

export const useGetRetailerNetworkUsersLazyQuery = (
  baseOptions?: QueryHookOptions<RetailerNetworkUsersQuery, RetailerNetworkUsersQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery(useRetailerNetworkUsersLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformDataToRetailerNetworkUsersArray(response.data) : response.data,
    },
  };
};

export const useGetRetailerNetworksForUserLazyQuery = (
  baseOptions?: QueryHookOptions<
    RetailerNetworksForUserQuery,
    RetailerNetworksForUserQueryVariables
  >,
) => {
  const [executeQuery, response] = useLazyQuery(useRetailerNetworksForUserLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformDataToRetailerNetworksArray(response.data) : response.data,
    },
  };
};

const transformDataToRetailerNetworkCustomerArray = (
  data: RetailerNetworkCustomerQuery,
): RetailerNetworkCustomerFragment[] => {
  const edges = data.retailerNetworkCustomer?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkCustomerLazy = (
  baseOptions?: LazyQueryHookOptions<
    RetailerNetworkCustomerQuery,
    RetailerNetworkCustomerQueryVariables
  >,
) => {
  const [executeQuery, response] = useLazyQuery(useRetailerNetworkCustomerLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data
        ? transformDataToRetailerNetworkCustomerArray(response.data)
        : response.data,
    },
  };
};

interface GetRetailerNetworkLocationsProps {
  filter: RetailerNetworkLocationFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationFragment[],
    RetailerNetworkLocationsQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformDataToRetailerNetworkLocationsArray = (
  data: RetailerNetworkLocationsQuery,
): RetailerNetworkLocationFragment[] => {
  const edges = data.retailerNetworkLocations?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocations = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworkLocationsProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationsQuery,
    'retailerNetworkLocations.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToRetailerNetworkLocationsArray(data));
        }
      },
    } as QueryHookOptions<RetailerNetworkLocationsQuery, RetailerNetworkLocationsQueryVariables>,
    paginationInfo,
    onPageChange,
  );

  const data = useMemo(
    () =>
      response.data ? transformDataToRetailerNetworkLocationsArray(response.data) : response.data,
    [response.data],
  );

  return {
    ...response,
    data,
  };
};

interface GetRetailerNetworkLocationDetailsProps {
  filter: RetailerNetworkLocationFilter;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationDetailsFragment[],
    RetailerNetworkLocationDetailsQueryVariables
  >;
}

const transformDataToRetailerNetworkLocationDetailsArray = (
  data: RetailerNetworkLocationDetailsQuery,
): RetailerNetworkLocationDetailsFragment[] => {
  const edges = data.retailerNetworkLocations?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationDetails = ({
  filter,
  baseOptions,
}: GetRetailerNetworkLocationDetailsProps) => {
  const response = useRetailerNetworkLocationDetailsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 1,
      filter,
    },
    ...baseOptions,
    onCompleted: (data) => {
      if (baseOptions && baseOptions.onCompleted) {
        baseOptions.onCompleted(transformDataToRetailerNetworkLocationDetailsArray(data));
      }
    },
  } as QueryHookOptions<RetailerNetworkLocationDetailsQuery, RetailerNetworkLocationDetailsQueryVariables> & { variables: RetailerNetworkLocationDetailsQueryVariables });

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationDetailsArray(response.data)
      : response.data,
  };
};

export const useGetRetailerNetworkLocationsLazyQuery = (
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationsQuery,
    RetailerNetworkLocationsQueryVariables
  >,
) => {
  const [executeQuery, response] = useLazyQuery(useRetailerNetworkLocationsLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data
        ? transformDataToRetailerNetworkLocationsArray(response.data)
        : response.data,
    },
  };
};

interface GetRetailerNetworkLocationGroupsProps {
  filter: RetailerNetworkLocationGroupFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationGroupFragment[],
    RetailerNetworkLocationGroupsQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformDataToRetailerNetworkLocationGroupsArray = (
  data: RetailerNetworkLocationGroupsQuery,
): RetailerNetworkLocationGroupFragment[] => {
  const edges = data.retailerNetworkLocationGroups?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationGroups = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworkLocationGroupsProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationGroupsQuery,
    'retailerNetworkLocationGroups.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToRetailerNetworkLocationGroupsArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationGroupsArray(response.data)
      : response.data,
  };
};

export const useGetRetailerNetworkLocationGroupsLazy = (
  baseOptions?: LazyQueryHookOptions<
    RetailerNetworkLocationGroupsQuery,
    RetailerNetworkLocationGroupsQueryVariables
  >,
) => {
  const [executeQuery, response] = useLazyQuery(
    useRetailerNetworkLocationGroupsLazyQuery,
    baseOptions,
  );

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data
        ? transformDataToRetailerNetworkLocationGroupsArray(response.data)
        : response.data,
    },
  };
};

type GetRetailerNetworkLocationsWithPerformanceExcerptProps = {
  filter: RetailerNetworkLocationsWithPerformanceExcerptFilter;
  sort?: RetailerNetworkPerformanceSort;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationWithPerformanceExcerptFragment[],
    RetailerNetworkLocationsWithPerformanceExcerptQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

type GetRetailerNetworkLocationsWithPerformanceExcerptReturnType = {
  data: RetailerNetworkLocationWithPerformanceExcerptFragment[] | undefined;
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
};

const transformDataToRetailerNetworkLocationsWithPerformanceExcerptArray = (
  data: RetailerNetworkLocationsWithPerformanceExcerptQuery,
): RetailerNetworkLocationWithPerformanceExcerptFragment[] => {
  const edges = data.retailerNetworkLocationsWithPerformanceExcerpt?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationsWithPerformanceExcerpt = ({
  filter,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworkLocationsWithPerformanceExcerptProps): GetRetailerNetworkLocationsWithPerformanceExcerptReturnType => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationsWithPerformanceExcerptQuery,
    'retailerNetworkLocationsWithPerformanceExcerpt.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
        sort,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(
            transformDataToRetailerNetworkLocationsWithPerformanceExcerptArray(data),
          );
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationsWithPerformanceExcerptArray(response.data)
      : response.data,
  };
};

export const useGetRetailerNetworkPerformanceQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkPerformanceQueryVariables>,
) =>
  useQuery(useApolloRetailerNetworkPerformanceQuery, {
    skip: !config.retailerConnect.enabled,
    ...baseOptions,
  });

export const useGetRetailerNetworkLocationDowntimeMetricsQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkLocationDowntimeMetricsQueryVariables>,
  skip?: boolean,
) => {
  const response = useQuery(useApolloRetailerNetworkLocationDowntimeMetricsQuery, {
    skip,
    ...baseOptions,
  });

  const data =
    response.data?.retailerNetworkLocationDowntimeMetrics.filter(
      (item: TimeSeriesMetricFragment) => !!item,
    ) || [];

  return { ...response, data };
};

export const useGetRetailerNetworkLocationDowntimeMetricsOverviewQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkLocationDowntimeMetricsOverviewQueryVariables>,
  skip?: boolean,
) => {
  const response = useQuery(useApolloRetailerNetworkLocationDowntimeMetricsOverviewQuery, {
    skip,
    ...baseOptions,
  });

  return { ...response, data: response.data?.retailerNetworkLocationDowntimeMetricsOverview };
};

export const useGetRetailerNetworkLocationGroupOnlineStatusQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkLocationGroupOnlineStatusQueryVariables>,
  skip?: boolean,
) => {
  const response = useQuery(useApolloRetailerNetworkLocationGroupOnlineStatusQuery, {
    ...baseOptions,
    ...(skip !== undefined ? { skip } : {}),
  });

  return { ...response, data: response.data?.retailerNetworkLocationDowntimeMetricsOverview };
};

export const useGetRetailerNetworkTransactionMetricsOverviewQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkTransactionMetricsOverviewQueryVariables>,
) => {
  const response = useQuery(useApolloRetailerNetworkTransactionMetricsOverviewQuery, baseOptions);
  return { ...response, data: response.data?.retailerNetworkTransactionMetricsOverview };
};

export const useGetRetailerNetworkLoyaltyMetricsOverviewQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkLoyaltyMetricsOverviewQueryVariables>,
) => {
  const response = useQuery(useApolloRetailerNetworkLoyaltyMetricsOverviewQuery, baseOptions);
  return { ...response, data: response.data?.retailerNetworkLoyaltyMetricsOverview };
};

type GetRetailerNetworkLocationOfferMetricsOverview = {
  filter: RetailerNetworkOfferMetricsOverviewFilter;
  sort?: RetailerNetworkOfferMetricsOverviewSort;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationOfferMetricsOverviewQuery,
    RetailerNetworkLocationOfferMetricsOverviewQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetRetailerNetworkLocationOfferMetricsOverviewQuery = ({
  filter,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworkLocationOfferMetricsOverview) => {
  const response = usePaginatedQuery(
    useApolloRetailerNetworkLocationOfferMetricsOverviewQuery,
    'retailerNetworkLocationOfferMetricsOverview.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
        sort,
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  const data = response.data
    ? response.data.retailerNetworkLocationOfferMetricsOverview.edges.map((item) => item.node) || []
    : response.data;

  return { ...response, data };
};

type GetRetailerNetworkLocationCoordinatesProps = {
  filter: RetailerNetworkLocationCoordinatesFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    RetailerNetworkLocationCoordinatesFragment[],
    RetailerNetworkLocationCoordinatesQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

const transformDataToRetailerNetworkLocationCoordinatesArray = (
  data: RetailerNetworkLocationCoordinatesQuery,
): RetailerNetworkLocationCoordinatesFragment[] => {
  const edges = data.retailerNetworkLocationCoordinates?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetRetailerNetworkLocationCoordinates = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetRetailerNetworkLocationCoordinatesProps) => {
  const response = usePaginatedQuery(
    useRetailerNetworkLocationCoordinatesQuery,
    'retailerNetworkLocationCoordinates.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToRetailerNetworkLocationCoordinatesArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformDataToRetailerNetworkLocationCoordinatesArray(response.data)
      : response.data,
  };
};

export const useGetRetailerNetworkLocationPosConnectionStatusQuery = (
  baseOptions?: BaseQueryOptions<RetailerNetworkLocationPosConnectionStatusQueryVariables>,
  skip?: boolean,
) => {
  const response = useQuery(useApolloRetailerNetworkLocationPosConnectionStatusQuery, {
    skip,
    ...baseOptions,
  });

  return { ...response, data: response.data?.retailerNetworkLocationPosConnectionStatus };
};
